import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { safeLocalStorage } from "@/lib/safeStorage";
import { useUserOrgContext } from "@/hooks/useUserOrgContext";

export interface OrgBranding {
  logo_light_url?: string | null;
  logo_dark_url?: string | null;
  logo_scale?: number;
  logo_position_x?: number;
  logo_position_y?: number;
  favicon_url?: string | null;
}

export interface Organization {
  id: string;
  name: string;
  crm_url?: string;
  created_at: string;
  updated_at: string;
  enabled_modules?: string[];
  plan_type?: string;
  partner_id?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  email_contato?: string | null;
  telefone?: string | null;
  endereco?: string | null;
  branding?: OrgBranding | null;
}

export interface OrgMember {
  id: string;
  org_id: string;
  user_id: string;
  role: 'proprietario' | 'gerente' | 'vendedor' | 'partner';
  created_at: string;
}

export function useOrganization() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: organizations = [], isLoading, isFetching, isInitialLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) return [];

      // Executar queries em paralelo para melhor performance
      const [
        { data: isAdmin }, 
        { data: memberships, error: memberError },
        { data: partnerId },
        { data: allowedOrgIds }
      ] = await Promise.all([
        supabase.rpc('is_admin', { _user_id: user.id }),
        supabase.from("org_members").select("org_id").eq("user_id", user.id),
        supabase.rpc('get_user_partner_id', { _user_id: user.id }),
        supabase.rpc('get_user_allowed_org_ids', { _user_id: user.id })
      ]);

      // Se for admin, buscar todas as organizações
      if (isAdmin) {
        const { data, error } = await supabase
          .from("orgs")
          .select("*")
          .order("name");

        if (error) throw error;
        return data as Organization[];
      }

      // Se for parceiro, buscar orgs do parceiro
      if (partnerId) {
        const { data, error } = await supabase
          .from("orgs")
          .select("*")
          .eq("partner_id", partnerId)
          .order("name");

        if (error) throw error;
        
        // Filtrar por allowed_org_ids se definido
        const orgs = data as Organization[];
        if (allowedOrgIds && allowedOrgIds.length > 0) {
          return orgs.filter(org => allowedOrgIds.includes(org.id));
        }
        
        return orgs;
      }

      // Se não for admin nem parceiro, buscar apenas as orgs que é membro
      if (memberError) throw memberError;

      const orgIds = (memberships || []).map((m) => m.org_id);
      if (orgIds.length === 0) return [];

      const { data, error } = await supabase
        .from("orgs")
        .select("*")
        .in("id", orgIds)
        .order("name");

      if (error) throw error;
      return data as Organization[];
    },
    staleTime: 300000, // OTIMIZAÇÃO: 5 minutos - dados raramente mudam
  });
  
  // Estado de carregamento combinado - inclui inicial loading para evitar flash
  const isLoadingOrgs = isLoading || isInitialLoading;

  const { data: currentOrgId, isLoading: isLoadingCurrentOrg } = useQuery({
    queryKey: ["current-org", organizations],
    queryFn: async () => {
      const stored = safeLocalStorage.getItem("current-org-id");
      
      // Validar se o stored ID existe nas organizações do usuário
      if (stored && organizations.some((o) => o.id === stored)) {
        return stored;
      }
      
      // Se não existir, limpar localStorage e usar primeira org
      if (stored) {
        console.warn("ID de organização inválido no localStorage. Limpando...");
        safeLocalStorage.removeItem("current-org-id");
      }
      
      const firstOrg = organizations[0]?.id || null;
      if (firstOrg) {
        safeLocalStorage.setItem("current-org-id", firstOrg);
      }
      return firstOrg;
    },
    enabled: organizations.length > 0,
  });

  const currentOrg = organizations.find((o) => o.id === currentOrgId);

  const setCurrentOrg = (orgId: string) => {
    safeLocalStorage.setItem("current-org-id", orgId);
    // OTIMIZAÇÃO: Invalidar corretamente e cascatear invalidações
    queryClient.invalidateQueries({ queryKey: ["current-org"] });
    // Invalidar dados dependentes da organização
    queryClient.invalidateQueries({ queryKey: ["leads"] });
    queryClient.invalidateQueries({ queryKey: ["vendedores"] });
    queryClient.invalidateQueries({ queryKey: ["pipelines"] });
    queryClient.invalidateQueries({ queryKey: ["funnel-stages"] });
    queryClient.invalidateQueries({ queryKey: ["lembretes"] });
    // Invalidar dados de tarefas para garantir visibilidade correta entre membros
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
    queryClient.invalidateQueries({ queryKey: ["task-subtasks"] });
    queryClient.invalidateQueries({ queryKey: ["task-activity"] });
    queryClient.invalidateQueries({ queryKey: ["org-members"] });
  };

  const updateOrg = useMutation({
    mutationFn: async ({ id, branding, ...updates }: Partial<Organization> & { id: string }) => {
      // Preparar dados para update, convertendo branding para JSON
      const updateData: Record<string, any> = { ...updates };
      if (branding !== undefined) {
        updateData.branding = branding as Record<string, any>;
      }
      
      const { data, error } = await supabase
        .from("orgs")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast({
        title: "Organização atualizada",
        description: "As alterações foram salvas com sucesso!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Flag para indicar se o usuário não tem organização
  // IMPORTANTE: Só considerar "sem organização" após carregamento completo
  const hasNoOrganization = !isLoadingOrgs && organizations.length === 0;

  return {
    organizations,
    currentOrg,
    currentOrgId,
    // CORREÇÃO: Usar isLoadingOrgs para evitar flash da tela "Nenhuma Organização"
    isLoading: isLoadingOrgs || isLoadingCurrentOrg,
    hasNoOrganization,
    setCurrentOrg,
    updateOrg,
  };
}
